import React, { useState, Suspense, lazy } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

// Layouts, Components & Hooks
import AdminLayout from '../../../layouts/AdminLayout';
import ErrorBoundary from '../../../shared/components/errors/ErrorBoundary';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import SearchComponent from '../../pms-agent/components/SearchComponent';
import { DynamicPricingGroupData } from '../../../services/dynamic-pricing-group/response/IndexDynamicPricingGroupResponse';
import { useToggleStatusDynamicPricingGroup } from '../../../services/dynamic-pricing-group/hooks/useToggleStatusDynamicPricingGroup';
import { useDeleteDynamicPricingGroup } from '../../../services/dynamic-pricing-group/hooks/useDeleteDynamicPricingGroup';
import useIndexDynamicPricingGroups from '../../../services/dynamic-pricing-group/hooks/useIndexDynamicPricingGroups';
import { useAuth } from '../../../auth/context/AuthProvider';
import { privateApi } from '../../../api/api';

// --- Lazy Loading Dialogs ---
const CreateDynamicPricingGroupDialog = lazy(() => import('../components/CreateDynamicPricingGroupDialog'));
const EditDynamicPricingGroupDialog = lazy(() => import('../components/EditDynamicPricingGroupDialog'));
const ConfirmationDialog = lazy(() => import('../../../shared/components/dialog/ConfirmationDialog'));
const AppliedItemsDialog = lazy(() => import('../components/AppliedItemsDialog'));
const RulesListDialog = lazy(() => import('../components/RulesListDialog'));
const PeriodsListDialog = lazy(() => import('../components/PeriodsListDialog'));
const GroupDetailsDialog = lazy(() => import('../components/GroupDetailsDialog'));

// Tipe untuk state konfirmasi yang menangani semua aksi
type ConfirmationAction = 'enable' | 'disable' | 'delete';
type ConfirmationState = {
    show: boolean;
    groupId: string | null;
    groupName: string;
    action: ConfirmationAction | null;
};

const DynamicPricingGroupPage: React.FC = () => {
    const { user } = useAuth();
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [confirmationState, setConfirmationState] = useState<ConfirmationState>({ show: false, groupId: null, groupName: '', action: null });

    // Details memakai objek group dari baris tabel apa adanya — query list sudah
    // `include: property,periods,items,rules`, jadi tidak perlu fetch ulang.
    const [viewingDetailsFor, setViewingDetailsFor] = useState<DynamicPricingGroupData | null>(null);
    const [viewingItemsFor, setViewingItemsFor] = useState<DynamicPricingGroupData | null>(null);
    const [viewingRulesFor, setViewingRulesFor] = useState<DynamicPricingGroupData | null>(null);
    const [viewingPeriodsFor, setViewingPeriodsFor] = useState<DynamicPricingGroupData | null>(null);

    const { data, isSuccess, isError, isLoading } = useIndexDynamicPricingGroups({
        property_id: user?.active_property_id,
        search: searchQuery,
        page: page,
        limit: 10,
        include: "property,periods,items,rules"
    });

    const queryClient = useQueryClient();
    const { mutate: toggleStatus, isPending: isTogglingStatus } = useToggleStatusDynamicPricingGroup();
    const { mutate: deleteGroup, isPending: isDeleting } = useDeleteDynamicPricingGroup();

    // Manual Sync Mutation
    const { mutate: manualSync, isPending: isSyncing } = useMutation({
        mutationFn: () => privateApi.get('/v3/setting/full-sync'), // TODO: Update endpoint if different for dynamic pricing
        onSuccess: () => {
            toast.success('Manual Sync has been initiated', {
                id: 'manual-sync',
            });
        },
        onError: (error) => {
            toast.error((error as any)?.response?.data?.message || 'Failed to initiate manual sync', {
                id: 'manual-sync',
            });
        }
    });

    // Handlers untuk dialog Create & Edit
    const handleOpenCreate = () => setShowCreateDialog(true);
    const handleCloseCreate = () => setShowCreateDialog(false);
    const handleOpenEdit = (id: string) => setEditingGroupId(id);
    const handleCloseEdit = () => setEditingGroupId(null);

    // Handler untuk manual sync
    const handleManualSync = () => {
        toast.loading('Initiating Manual Sync...', {
            id: 'manual-sync',
        });
        manualSync();
    };

    // Handlers untuk dialog konfirmasi
    const handleOpenConfirmation = (groupId: string, action: ConfirmationAction, groupName: string) => {
        setConfirmationState({ show: true, groupId, action, groupName });
    };
    const handleCloseConfirmation = () => {
        if (isTogglingStatus || isDeleting) return;
        setConfirmationState({ show: false, groupId: null, groupName: '', action: null });
    };

    const handleCloseViewDetails = () => setViewingDetailsFor(null);
    const handleCloseViewItems = () => setViewingItemsFor(null);
    const handleCloseViewRules = () => setViewingRulesFor(null);
    const handleCloseViewPeriods = () => setViewingPeriodsFor(null);

    // Handler utama untuk mengeksekusi aksi setelah konfirmasi
    const handleConfirm = () => {
        const { groupId, action } = confirmationState;
        if (!groupId || !action) return;

        if (action === 'enable' || action === 'disable') {
            const toastId = `toggle-${action}-${groupId}`;
            toast.loading(`${action === 'enable' ? 'Enabling' : 'Disabling'} group...`, { id: toastId });

            toggleStatus({ groupId, status: action }, {
                onSuccess: async () => {
                    toast.success(`Group ${action}d successfully`, { id: toastId });

                    // Mengaktifkan group TIDAK otomatis mendorong harga baru ke channel
                    // manager — user harus menekan "Sync Now". Tanpa pengingat ini, kasus
                    // "sudah diaktifkan tapi harga di OTA tidak berubah" gampang terjadi.
                    // Sengaja hanya saat 'enable' (bukan 'disable') dan durasinya panjang
                    // supaya tidak keburu hilang sebelum dibaca.
                    if (action === 'enable') {
                        toast.info('Dynamic pricing is active. Click "Sync Now" to push the new rates to your channels.', {
                            id: `sync-reminder-${groupId}`,
                            duration: 12000,
                            action: {
                                label: 'Sync Now',
                                onClick: () => handleManualSync(),
                            },
                        });
                    }

                    await queryClient.refetchQueries({ queryKey: ['dynamic-pricing-groups'] });
                    handleCloseConfirmation();
                },
                onError: (error: any) => {
                    toast.error(error.message || `Failed to ${action} group`, { id: toastId });
                }
            });
        } else if (action === 'delete') {
            const toastId = `delete-${groupId}`;
            toast.loading(`Deleting group...`, { id: toastId });

            deleteGroup({ groupId }, {
                onSuccess: async () => {
                    toast.success(`Group deleted successfully`, { id: toastId });
                    await queryClient.refetchQueries({ queryKey: ['dynamic-pricing-groups'] });
                    handleCloseConfirmation();
                },
                onError: (error: any) => {
                    toast.error(error.message || `Failed to delete group`, { id: toastId });
                }
            });
        }
    };

    const isActionPending = isTogglingStatus || isDeleting;

    return (
        <AdminLayout>
            <ErrorBoundary>
                <div className="container-fluid mt-5">
                    <div className="table-container">
                        <h3 className="mb-2">Dynamic Pricing Groups</h3>
                        {/* Pengingat permanen: toast setelah aktivasi bisa keburu hilang
                            atau terlewat, jadi informasinya tetap tersedia di halaman. */}
                        <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                            After activating or changing a group, click <strong>Sync Now</strong> to push the updated rates to your channels.
                        </p>
                        <div className="search-container mb-3">
                            <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                            <div>
                                <button
                                    className="btn btn-secondary me-2"
                                    disabled={isSyncing}
                                    onClick={handleManualSync}
                                >
                                    {isSyncing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Syncing...
                                        </>
                                    ) : (
                                        "Sync Now"
                                    )}
                                </button>
                                <button className="btn btn-primary" onClick={handleOpenCreate}>
                                    Create
                                </button>
                            </div>
                        </div>

                        <div>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Calculation Method</th>
                                        <th>Applied To</th>
                                        <th>Rules / Periods</th>
                                        <th>Status</th>
                                        <th className='text-end'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && <tr><td colSpan={5} className="text-center">Loading...</td></tr>}
                                    {isError && <tr><td colSpan={5} className="text-center text-danger">Failed to load data.</td></tr>}
                                    {isSuccess && data.data.length === 0 && <tr><td colSpan={6} className="text-center">No groups found.</td></tr>}
                                    {isSuccess && data.data.map((group: DynamicPricingGroupData) => (
                                        <tr key={group.id}>
                                            <td>
                                                <div>{group.name}</div>
                                                <small className="text-muted">{group.property?.name || 'No Property'}</small>
                                            </td>
                                            <td><span className="badge bg-secondary text-white">{group.calculation_method.replace('_', ' ')}</span></td>
                                            <td>
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-decoration-underline"
                                                    onClick={() => setViewingItemsFor(group)}
                                                    disabled={!group.items || group.items.length === 0}
                                                >
                                                    {group.items?.length || 0} Room Type(s)
                                                </Button>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-decoration-underline me-2"
                                                    onClick={() => setViewingRulesFor(group)}
                                                    disabled={!group.rules || group.rules.length === 0}
                                                >
                                                    {group.rules?.length || 0} Rules
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-decoration-underline text-secondary"
                                                    onClick={() => setViewingPeriodsFor(group)}
                                                    disabled={!group.periods || group.periods.length === 0}
                                                >
                                                    {group.periods?.length || 0} Periods
                                                </Button>
                                            </td>
                                            <td>
                                                {group.is_active
                                                    ? <span className="badge bg-success text-white">Active</span>
                                                    : <span className="badge bg-danger text-white">Inactive</span>
                                                }
                                            </td>
                                            <td className="text-end">
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="default" id={`dropdown-group-${group.id}`}>Actions</Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        {/* Details bersifat read-only, jadi tetap tersedia walau group aktif
                                                            (berbeda dengan Edit yang di-disable saat aktif). */}
                                                        <Dropdown.Item onClick={() => setViewingDetailsFor(group)}>Details</Dropdown.Item>
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item onClick={() => handleOpenEdit(group.id)} disabled={group.is_active}>Edit</Dropdown.Item>
                                                        {group.is_active ? (
                                                            <Dropdown.Item className="text-warning" onClick={() => handleOpenConfirmation(group.id, 'disable', group.name)}>
                                                                Disable
                                                            </Dropdown.Item>
                                                        ) : (
                                                            <Dropdown.Item className="text-success" onClick={() => handleOpenConfirmation(group.id, 'enable', group.name)}>
                                                                Enable
                                                            </Dropdown.Item>
                                                        )}
                                                        <Dropdown.Item className='text-danger' onClick={() => handleOpenConfirmation(group.id, 'delete', group.name)}>
                                                            Remove
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {isSuccess && data.pagination && data.data.length > 0 && (
                            <PaginationComponent
                                totalPages={data.pagination.last_page}
                                currentPage={data.pagination.current_page}
                                onPageChange={setPage}
                            />
                        )}
                    </div>
                </div>
            </ErrorBoundary>

            <Suspense fallback={<div className="modal-backdrop fade show"></div>}>
                {showCreateDialog && (
                    <CreateDynamicPricingGroupDialog show={showCreateDialog} onClose={handleCloseCreate} />
                )}
                {editingGroupId && (
                    <EditDynamicPricingGroupDialog
                        show={!!editingGroupId}
                        onClose={handleCloseEdit}
                        groupId={editingGroupId}
                    />
                )}
                {confirmationState.show && (
                    <ConfirmationDialog
                        show={confirmationState.show}
                        onClose={handleCloseConfirmation}
                        onConfirm={handleConfirm}
                        title={`${confirmationState.action?.charAt(0).toUpperCase() + confirmationState.action!.slice(1)} Group`}
                        message={
                            <p>
                                Are you sure you want to <strong>{confirmationState.action}</strong> group
                                named <strong>"{confirmationState.groupName}"</strong>?
                            </p>
                        }
                        confirmButtonText={confirmationState.action?.charAt(0).toUpperCase() + confirmationState.action!.slice(1)}
                        confirmButtonVariant={confirmationState.action === 'delete' ? 'danger' : (confirmationState.action === 'enable' ? 'success' : 'warning')}
                        isConfirming={isActionPending}
                    />
                )}
                {viewingDetailsFor && (
                    <GroupDetailsDialog
                        show={!!viewingDetailsFor}
                        onClose={handleCloseViewDetails}
                        group={viewingDetailsFor}
                    />
                )}
                {viewingItemsFor && (
                    <AppliedItemsDialog
                        show={!!viewingItemsFor}
                        onClose={handleCloseViewItems}
                        group={viewingItemsFor}
                    />
                )}
                {viewingRulesFor && (
                    <RulesListDialog
                        show={!!viewingRulesFor}
                        onClose={handleCloseViewRules}
                        group={viewingRulesFor}
                    />
                )}
                {viewingPeriodsFor && (
                    <PeriodsListDialog
                        show={!!viewingPeriodsFor}
                        onClose={handleCloseViewPeriods}
                        group={viewingPeriodsFor}
                    />
                )}
            </Suspense>
        </AdminLayout>
    );
};

export default DynamicPricingGroupPage;